import cn from 'clsx'
import { intervalToDuration } from 'date-fns'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { Trade } from '@App/redux/slices/trade/trades/types'

import { Card } from '../Card'

import s from './styles.module.scss'
import {useAppSelector} from "@App/hooks";
import {overallAnalytics} from "@Redux/slices/reports/selectors";
import {currentWallet} from "@Redux/slices/user/selectors";

const formatTimestampDiff = (seconds: number | null) => {
  if (seconds == null || seconds <= 0) return '0m'

  const totalMinutes = Math.floor(seconds / 60)

  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}

const formatDurationDF = (start: Date, end: Date) => {
  const {
    days = 0,
    hours = 0,
    minutes = 0,
  } = intervalToDuration({
    start,
    end,
  })

  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
}
const averageHoldingTimeDF = (trades: any[]) => {
  if (trades.length === 0) return '-'

  const durations = trades.map((t) => {
    const start = new Date(t.open_time)
    const end = new Date(t.close_time)
    return end.getTime() - start.getTime()
  })

  const avgMs = durations.reduce((sum, v) => sum + v, 0) / durations.length

  return formatDurationDF(0 as any, avgMs as any)
}

export const AverageHoldingTime = () => {
  const analytics = useAppSelector(overallAnalytics)

  return (
    <Card className={s.container} title="Average holding time" contentClassName={s.content}>
      <div className={s.row}>
        <div className={s.title}>Profitable</div>
        <div className={s.value}>{formatTimestampDiff(analytics?.avg_holding_profit ?? 0)}</div>
      </div>
      <div className={s.row}>
        <div className={s.title}>Loss making</div>
        <div className={s.value}>{formatTimestampDiff(analytics?.avg_holding_loss ?? 0)}</div>
      </div>
      <div className={s.row}>
        <div className={s.title}>Combined</div>
        <div className={s.value}>{formatTimestampDiff(analytics?.avg_holding_all ?? 0)}</div>
      </div>
    </Card>
  )
}
export const AverageReturn = () => {
  const wallet = useAppSelector(currentWallet)
  const analytics = useAppSelector(overallAnalytics)

  const averageReturn = (trades: Trade[]) => {
    if (trades.length === 0) return 0

    const sum = trades.reduce((acc, t) => acc + t.result, 0)
    return sum / trades.length
  }

  return (
    <Card className={s.container} title="Average return" contentClassName={s.content}>
      <div className={s.row}>
        <div className={s.title}>Profitable</div>
        <div className={cn(s.value, (analytics?.avg_return_profit ?? 0) > 0 && s.positive)}>
          {analytics?.avg_return_profit ? getValueWithCurrency(wallet?.currency_code ?? 'USD', analytics.avg_return_profit) : '-'}
        </div>
      </div>
      <div className={s.row}>
        <div className={s.title}>Loss making</div>
        <div className={cn(s.value, (analytics?.avg_return_loss ?? 0) < 0 && s.negative)}>
          {analytics?.avg_return_loss ? getValueWithCurrency(wallet?.currency_code ?? 'USD', analytics.avg_return_loss) : '-'}
        </div>
      </div>
      <div className={s.row}>
        <div className={s.title}>Combined</div>
        <div className={cn(s.value, (analytics?.avg_return_all ?? 0) < 0 ? s.negative : s.positive)}>
          {analytics?.avg_return_all ? getValueWithCurrency(wallet?.currency_code ?? 'USD', analytics.avg_return_all) : '-'}
        </div>
      </div>
    </Card>
  )
}
